import { useState } from 'react'
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator'
import {
  ImagePickerAsset,
  launchImageLibraryAsync,
  MediaTypeOptions,
  ImagePickerOptions,
} from 'expo-image-picker'

type UseImagePickerProps = {
  onChange?: (file: ImagePickerAsset) => void
  onError?: (error: string) => void
  options?: ImagePickerOptions
  initialValue?: ImagePickerAsset | null
  constraints?: {
    minWidth?: number
    minHeight?: number
  }
  resize?: {
    width?: number
    height?: number
  }
}

export const useImagePicker = ({
  onChange,
  onError,
  options,
  initialValue = null,
  resize,
  constraints,
}: UseImagePickerProps) => {
  const [image, setImage] = useState<ImagePickerAsset | null>(initialValue)
  const [loading, setLoading] = useState<boolean>(false)

  const pickImage = async () => {
    const result = await launchImageLibraryAsync({
      mediaTypes: MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 1,
      aspect: [1, 1],
      ...options,
    })

    if (result.canceled) {
      return
    }

    let image = result.assets[0]
    if (!image) {
      return
    }

    const { minWidth, minHeight } = constraints || {}

    if (minWidth && image.width < minWidth) {
      onError && onError(`Image width must be at least ${minWidth}px`)
      return
    }

    if (minHeight && image.height < minHeight) {
      onError && onError(`Image height must be at least ${minHeight}px`)
      return
    }

    if (resize && (resize.width || resize.height)) {
      setLoading(true)
      const { width, height } = resize

      // Don't upscale
      const resizeToWidth = width && image.width < width ? image.width : width
      const resizeToHeight = height && image.height < height ? image.height : height

      const resizedImage = await manipulateAsync(
        image.uri,
        [
          {
            resize: {
              width: resizeToWidth,
              height: resizeToHeight,
            },
          },
        ],
        { format: SaveFormat.PNG }
      )

      if (resizedImage.uri) {
        image = resizedImage
      }
      setLoading(false)
    }

    setImage(image)
    onChange && onChange(image)
  }

  return {
    image,
    pickImage,
    loading,
  }
}
